import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Brain, Settings, FileText, Zap, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ExtractionConfig {
  confidence_threshold: number;
  retry_attempts: number;
  enable_ocr_fallback: boolean;
  enable_multi_tier: boolean;
  preferred_providers: string[];
  validation_rules: {
    require_amount: boolean;
    require_due_date: boolean;
    require_account_number: boolean;
    require_supplier_name: boolean;
  };
}

const AdvancedExtractionInterface = () => {
  const [config, setConfig] = useState<ExtractionConfig>({
    confidence_threshold: 70,
    retry_attempts: 2,
    enable_ocr_fallback: true,
    enable_multi_tier: true,
    preferred_providers: ['openai', 'mistral'],
    validation_rules: {
      require_amount: true,
      require_due_date: false,
      require_account_number: true,
      require_supplier_name: true
    }
  });

  const [testResults, setTestResults] = useState<any>(null);
  const [isTestingEngine, setIsTestingEngine] = useState(false);
  const { toast } = useToast();

  const aiProviders = [
    { id: 'openai', name: 'OpenAI GPT-4o-mini', status: 'active', reliability: 95 },
    { id: 'mistral', name: 'Mistral Large', status: 'active', reliability: 88 },
    { id: 'openrouter', name: 'Claude 3.5 Sonnet', status: 'limited', reliability: 92 },
    { id: 'deepseek', name: 'DeepSeek Chat', status: 'limited', reliability: 78 }
  ];

  const handleConfigUpdate = (key: keyof ExtractionConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleValidationRuleUpdate = (rule: string, value: boolean) => {
    setConfig(prev => ({
      ...prev,
      validation_rules: {
        ...prev.validation_rules,
        [rule]: value
      }
    }));
  };

  const handleProviderToggle = (providerId: string) => {
    setConfig(prev => ({
      ...prev,
      preferred_providers: prev.preferred_providers.includes(providerId)
        ? prev.preferred_providers.filter(id => id !== providerId)
        : [...prev.preferred_providers, providerId]
    }));
  };

  const testExtractionEngine = async () => {
    setIsTestingEngine(true);
    
    try {
      // Test the multi-tier parser with current config
      const response = await supabase.functions.invoke('test-extraction-engine', {
        body: { config }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setTestResults(response.data);
      toast({
        title: "Engine Test Complete",
        description: "Extraction engine test completed successfully.",
      });
    } catch (error) {
      toast({
        title: "Test Failed",
        description: "Failed to test extraction engine.",
        variant: "destructive"
      });
    } finally {
      setIsTestingEngine(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      // Save configuration using localStorage for now
      localStorage.setItem('extraction_config', JSON.stringify(config));

      toast({
        title: "Configuration Saved",
        description: "Extraction engine configuration has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Failed to save configuration.",
        variant: "destructive"
      });
    }
  };

  const getProviderStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'limited': return 'secondary';
      case 'inactive': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Brain className="h-5 w-5" />
            <span>Advanced Extraction Engine</span>
          </CardTitle>
          <CardDescription>
            Configure and manage the AI-powered bill extraction system
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="configuration" className="space-y-4">
        <TabsList>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="providers">AI Providers</TabsTrigger>
          <TabsTrigger value="testing">Engine Testing</TabsTrigger>
          <TabsTrigger value="monitoring">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="configuration" className="space-y-4">
          <div className="grid gap-4">
            {/* Extraction Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Extraction Settings</CardTitle>
                <CardDescription>
                  Configure how the extraction engine processes bills
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Confidence Threshold (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={config.confidence_threshold}
                      onChange={(e) => handleConfigUpdate('confidence_threshold', parseInt(e.target.value))}
                    />
                    <p className="text-sm text-muted-foreground">
                      Minimum confidence required for auto-processing
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Retry Attempts</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={config.retry_attempts}
                      onChange={(e) => handleConfigUpdate('retry_attempts', parseInt(e.target.value))}
                    />
                    <p className="text-sm text-muted-foreground">
                      Number of retry attempts for failed extractions
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>OCR Fallback</Label>
                      <div className="text-sm text-muted-foreground">
                        Use OCR when text extraction fails
                      </div>
                    </div>
                    <Switch
                      checked={config.enable_ocr_fallback}
                      onCheckedChange={(value) => handleConfigUpdate('enable_ocr_fallback', value)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Multi-Tier Processing</Label>
                      <div className="text-sm text-muted-foreground">
                        Try multiple AI providers for better accuracy
                      </div>
                    </div>
                    <Switch
                      checked={config.enable_multi_tier}
                      onCheckedChange={(value) => handleConfigUpdate('enable_multi_tier', value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validation Rules */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Validation Rules</CardTitle>
                <CardDescription>
                  Define which fields are required for successful extraction
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(config.validation_rules).map(([rule, enabled]) => (
                  <div key={rule} className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="capitalize">
                        {rule.replace('require_', '').replace('_', ' ')}
                      </Label>
                      <div className="text-sm text-muted-foreground">
                        Require this field for successful processing
                      </div>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(value) => handleValidationRuleUpdate(rule, value)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={testExtractionEngine} disabled={isTestingEngine}>
                <Zap className="h-4 w-4 mr-2" />
                Test Engine
              </Button>
              <Button onClick={saveConfiguration}>
                <Settings className="h-4 w-4 mr-2" />
                Save Configuration
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AI Provider Configuration</CardTitle>
              <CardDescription>
                Select and configure AI providers for bill extraction
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {aiProviders.map((provider) => (
                  <div key={provider.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={config.preferred_providers.includes(provider.id)}
                        onChange={() => handleProviderToggle(provider.id)}
                        className="w-4 h-4"
                      />
                      <div>
                        <h4 className="font-medium">{provider.name}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant={getProviderStatusColor(provider.status)}>
                            {provider.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {provider.reliability}% reliability
                          </span>
                        </div>
                      </div>
                    </div>
                    <Progress value={provider.reliability} className="w-24" />
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Providers are tried in order. If one fails, the system automatically tries the next available provider.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="testing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Engine Testing</CardTitle>
              <CardDescription>
                Test the extraction engine with current configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={testExtractionEngine}
                disabled={isTestingEngine}
                className="w-full"
              >
                {isTestingEngine ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Testing Engine...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Run Engine Test
                  </>
                )}
              </Button>

              {testResults && (
                <div className="space-y-3 mt-4">
                  <h4 className="font-medium">Test Results</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 border rounded">
                      <div className="text-2xl font-bold text-success">
                        {testResults.success_rate}%
                      </div>
                      <div className="text-sm text-muted-foreground">Success Rate</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-2xl font-bold">
                        {testResults.avg_confidence}%
                      </div>
                      <div className="text-sm text-muted-foreground">Avg Confidence</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-2xl font-bold">
                        {testResults.avg_time}s
                      </div>
                      <div className="text-sm text-muted-foreground">Avg Time</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monitoring" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Performance Monitoring</CardTitle>
              <CardDescription>
                Real-time performance metrics for the extraction engine
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Performance monitoring will be displayed here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Real-time metrics, provider health, and extraction trends
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedExtractionInterface;